import { Link } from 'react-router';

import { cx } from '../../../shared/lib/cx.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import type { SetupProgress } from '../data/setupRepository.js';
import { useSetupProgress } from '../hooks/useSetupProgress.js';
import styles from './SetupPage.module.css';

/**
 * "Thiết lập ban đầu" — the go-live checklist (plan step 1.9, spec v2 §27).
 *
 * A guide, not a fifth copy of four screens (PO decision 2026-08-31). Accounts, debts, income
 * sources, recurring templates and the reserve policy each already have a screen built for
 * living with them for years; a wizard with its own forms would be two implementations of
 * every one of them, both needing to be changed together forever.
 *
 * So this screen does the two things the other screens cannot: it says WHAT has to be
 * declared at go-live — the list is real, from the owner's own spreadsheet — and it reads the
 * database to show what is already there. Nothing here stores progress: the tick comes from
 * the data, so it cannot claim a step is done after the account behind it was archived.
 */
export function SetupPage() {
  const state = useSetupProgress();

  if (state.status === 'loading') return <LoadingState label="Đang xem bạn đã khai những gì…" />;
  if (state.status === 'error') {
    return <ErrorState message={state.message} onRetry={state.retry} />;
  }

  const { progress } = state;
  const steps = stepsOf(progress);
  const doneCount = steps.filter((step) => step.done && !step.optional).length;
  const requiredCount = steps.filter((step) => !step.optional).length;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Thiết lập ban đầu</h1>

      <p className={styles.lead}>
        Bốn nhóm việc cần khai một lần khi bắt đầu dùng thật. Dấu tích được đọc từ dữ liệu thật
        trong hệ, không phải từ một nút "đã xong" — nên nó luôn nói đúng hiện trạng.
      </p>
      <p className={styles.progress} role="status">
        Đã xong {doneCount}/{requiredCount} nhóm bắt buộc
      </p>

      {steps.map((step, index) => (
        <StepCard key={step.title} step={step} index={index + 1} />
      ))}
    </div>
  );
}

type Step = Readonly<{
  title: string;
  done: boolean;
  optional?: boolean;
  /** What the data says right now. */
  status: string;
  /** What has to be declared — the real list from the owner's spreadsheet. */
  items: readonly string[];
  hint?: string;
  link?: { to: string; label: string };
}>;

/**
 * The list in `items` mirrors the GROUPS of the "Init data" section of
 * agents-doc/plans/phase-1-personal-finance.md, but with generic examples: this screen is
 * shared by every household, so no real account, person, or property name of the owner may
 * appear here (PO, 2026-09-17). Keep the groups in step — a checklist that forgets one kind of
 * wallet produces a report that is quietly wrong about how much the family has.
 */
function stepsOf(progress: SetupProgress): readonly Step[] {
  return [
    {
      title: 'Tài khoản + số dư thật hôm nay',
      done: progress.assetAccounts > 0 && progress.accountsWithOpening > 0,
      status:
        progress.assetAccounts === 0
          ? 'Chưa có tài khoản nào'
          : `${progress.assetAccounts} tài khoản · ${progress.accountsWithOpening} đã khai số dư đầu`,
      items: [
        'Tiền mặt · tài khoản nhận lương · tài khoản tiết kiệm · tài khoản quỹ dự phòng',
        'MoMo — đặt cách ghi "Nhập tổng cuối tháng"',
        'Tài khoản tiền và cổ phiếu tại công ty chứng khoán (Phase 3 mới dùng tới)',
        'Bất động sản, vàng… (tài sản khác)',
      ],
      hint:
        'Số dư khai ở đây là con số DUY NHẤT trong hệ được coi là đúng — mọi báo cáo về sau ' +
        'đều dựa vào nó. Khai đúng số thật tại ngày bắt đầu, không phải số tròn.',
      link: { to: '/tai-san', label: 'Mở màn Tài sản' },
    },
    {
      title: 'Các khoản nợ',
      done: progress.liabilityAccounts > 0,
      status:
        progress.liabilityAccounts === 0
          ? 'Chưa khai khoản nợ nào'
          : `${progress.liabilityAccounts} khoản nợ`,
      items: [
        'Vay ngân hàng · vay người thân · thẻ tín dụng',
        'Nợ mua nhà/đất còn phải trả (mỗi chủ nợ một khoản)',
      ],
      hint:
        'Mỗi khoản nợ là một tài khoản loại "Nợ", số dư = phần CÒN LẠI phải trả. Khoản nào ' +
        'trả góp hằng tháng thì thêm luôn một khoản định kỳ ở nhóm 3. Không có nợ nào thì bỏ ' +
        'qua nhóm này.',
      link: { to: '/tai-san', label: 'Mở màn Tài sản' },
    },
    {
      title: 'Nguồn thu, khoản định kỳ, quỹ dự phòng',
      done: progress.incomeSources > 0 && progress.recurringTemplates > 0,
      status:
        `${progress.incomeSources} nguồn thu · ${progress.recurringTemplates} khoản định kỳ · ` +
        `${progress.reservePolicies} chính sách dự phòng`,
      items: [
        'Nguồn thu: Lương (ổn định) · Hoàn thuế (không đều)',
        'Định kỳ: tiền nhà · điện · nước · internet · bảo hiểm · từ thiện',
        'Định kỳ: chia lương (mẫu nhiều nhánh) · trả nợ theo kỳ (hằng tháng / hằng năm)',
        'Quỹ dự phòng: mỗi tháng góp bao nhiêu, vào tài khoản nào',
      ],
      hint:
        'Nguồn thu nào để "ổn định" thì mới được tính vào số tiền có thể đầu tư an toàn (B10) ' +
        '— hoàn thuế cố tình KHÔNG ổn định.',
      link: { to: '/dinh-ky', label: 'Mở màn Khoản định kỳ' },
    },
    {
      title: 'Nhập lịch sử các năm cũ',
      done: progress.legacyImports > 0,
      optional: true,
      status:
        progress.legacyImports === 0
          ? 'Chưa nhập lịch sử'
          : `${progress.legacyImports} lần nhập lịch sử`,
      items: [
        'Cách dễ nhất: nhờ AI agent (Claude Code, Codex, Gemini…) làm — dán bảng thu/chi các ' +
          'năm cũ vào phiên chat, agent dùng tool "legacy_import_history" để nhập.',
        'Agent luôn chạy thử trước và đưa bảng đối chiếu; bạn đọc rồi gõ mã xác nhận thì mới ' +
          'ghi vào sổ — agent không tự ghi được.',
        'Tự làm bằng dòng lệnh: pnpm pfo legacy-import <file.md> --months 2025-01..2026-08 --dry-run',
      ],
      hint:
        'Tuỳ chọn, và chỉ để có dữ liệu XU HƯỚNG cho các năm trước. Nó không đụng số dư tài ' +
        'khoản nào — các dòng tiết kiệm/đầu tư/trả nợ trong file cũ cố tình không được nhập, ' +
        'vì biểu diễn chúng cần chạm vào số dư thật. Hướng dẫn đầy đủ: tools/pfo-cli/README.md.',
    },
  ];
}

function StepCard({ step, index }: { step: Step; index: number }) {
  return (
    <section className={lists.card} aria-labelledby={`setup-step-${index}`}>
      <h2 className={cx(lists.cardTitle, styles.stepTitle)} id={`setup-step-${index}`}>
        <span className={cx(styles.mark, step.done && styles.markDone)} aria-hidden="true">
          {step.done ? '✓' : index}
        </span>
        {step.title}
        {step.optional ? <span className={styles.badge}>tuỳ chọn</span> : null}
      </h2>

      <p className={cx(styles.status, step.done && styles.statusDone)}>{step.status}</p>

      <ul className={styles.items}>
        {step.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      {step.hint ? <p className={forms.hint}>{step.hint}</p> : null}

      {step.link ? (
        <Link className={styles.action} to={step.link.to}>
          {step.link.label} →
        </Link>
      ) : null}
    </section>
  );
}
